import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import containsKeyword from '../helper/containsKeyword';
import useLadyService from '@/services/LadyService';
import { setProfileCount } from '@/stores/slices/mapModelSlice';
import { getIsMainPage, resetPage } from '@/stores/slices/modelsState';
import {
	getUserCountry,
	setCity,
	setCountriesData,
	setCurrentCity,
	setLanguagesWords,
	setNationalistWords,
	setServicesWords,
	setCountry,
	setCountrySlug,
	setRegion,
	getUserCurrentCity,
	setLinksCurrentCity,
	setUserCurrentDistrict,
} from '@/stores/slices/userSlice';

import { makeRequest } from '@/services/makeRequest';
import { getCanonicalCountrySlug } from '@/helper/linkCitySlug';

const useDefiningCity = (setIsLoading = false) => {
	const { dispatch, city, navigate, lang, usePrevious } = useLadyService();

	const allCities = useSelector(getUserCountry);
	const currentCity = useSelector(getUserCurrentCity);
	const slugOfAllCities = allCities.map((city) => city.slug);
	const { city: cityURLParam } = useParams();
	const cityParam = cityURLParam?.split('_')[0];
	const districtParam = cityURLParam?.split('_')[1];

	const isMainPage = useSelector(getIsMainPage);
	if (setIsLoading) {
		setIsLoading(true);
	}
	const prevLang = usePrevious(lang);
	const location = useLocation();

	const timeoutRef = useRef(null);

	const loadServices = async () => {
		const route = 'services';
		const method = 'GET';

		await makeRequest({ route, method })
			.then((response) => {
				const data = response.data;
				if (!!Object.values(data).length) {
					const citiesData = data.cities;
					const languages = data.languages;
					const nationalities = data.nationalities;
					const services = data.services;
					const storedPreferredCountrySlug =
						typeof window !== 'undefined'
							? localStorage.getItem('preferredCountrySlug')
							: null;
					const preferredCountrySlug = getCanonicalCountrySlug(
						storedPreferredCountrySlug,
						citiesData,
					);
					const routeCountryEntry = Object.entries(citiesData).find(
						([key, country]) =>
							[key, country?.code].some(
								(value) =>
									String(value || '').toLowerCase() ===
									String(cityParam || '').toLowerCase(),
							) && country?.slug,
					);
					const country =
						cityParam
							? Object.keys(citiesData).find(
								(key) => citiesData[key].slug === 'czechia',
							) ?? Object.keys(citiesData)[0]
							: Object.keys(citiesData).find(
								(key) =>
									citiesData[key].slug === preferredCountrySlug,
							) ??
							(citiesData.cz ? 'cz' : Object.keys(citiesData)[0]);
					const countrySLug = citiesData[country].slug;
					dispatch(setLanguagesWords(languages));
					dispatch(setNationalistWords(nationalities));
					dispatch(setServicesWords(services));
					// here to add districts if nesessery
					dispatch(setCountriesData(citiesData));
					if (
						routeCountryEntry &&
						cityParam !== routeCountryEntry[1].slug
					) {
						const pathSegments = location.pathname.split('/');
						const hasLanguagePrefix = [
							'en',
							'cz',
							'ru',
							'de',
							'sk',
						].includes(pathSegments[1]);
						const routeCityIndex = hasLanguagePrefix ? 2 : 1;
						if (pathSegments[routeCityIndex] === cityURLParam) {
							pathSegments[routeCityIndex] =
								`${routeCountryEntry[1].slug}${cityURLParam.includes('_') ? `_${cityURLParam.split('_').slice(1).join('_')}` : ''}`;
							navigate(
								`${pathSegments.join('/')}${location.search}${location.hash}`,
								{ replace: true },
							);
							return;
						}
					}
					const countryFromSlug = Object.keys(citiesData).find(
						(key) => citiesData[key].slug === cityParam,
					);
					const countryFromCity = Object.keys(citiesData).find((key) =>
						citiesData[key].cities?.some(
							(item) => item.slug === cityParam,
						),
					);
					const selectedCountryKey =
						countryFromSlug ?? countryFromCity ?? country;
					const selectedCountry =
						citiesData[selectedCountryKey];
					const selectedCountrySlug = selectedCountry.slug;
					const citySegment = cityParam || selectedCountrySlug;
					let cities;
					if (selectedCountrySlug === citySegment) {
						// check for districts
						dispatch(setCountrySlug(selectedCountrySlug));
						cities = Object.keys(citiesData).map((key) => {
							const item = citiesData[key];
							return {
								id: item?.id ?? 1,
								title: item.name,
								icon: item.code.toUpperCase(),
								highlight: item.highlight,
								slug: item.slug,
								profiles_count: item?.profiles_count ?? 0,
								sort: item.sort,
								districts: item.districts ?? [],
							};
						});
					} else {
						let cityExist = false;
						// check for districts
						dispatch(setCountrySlug(false));
						cities = selectedCountry.cities.map((item) => {
							if (!cityExist && item.slug === citySegment) {
								cityExist = true;
							}
							return {
								id: item.id,
								title: item.name,
								icon: selectedCountry.code.toUpperCase(),
								highlight: item.highlight,
								slug: item.slug,
								profiles_count: item.profiles_count,
								sort: item.sort,
								districts: item.districts ?? [],
							};
						});
						if (!cityExist && isMainPage) {
							navigate('/404');
							return;
						}
					}
					let newCity;
					if (citySegment === selectedCountrySlug) {
						newCity = cities.find(
							(item) => item.slug === citySegment,
						);
					} else {
						newCity = cities.find(
							(item) => item.slug === citySegment,
						);
					}
					if (newCity) {
						dispatch(setProfileCount(newCity.profiles_count));
						dispatch(setCurrentCity(newCity));
						// dispatch(resetPage());
						if (districtParam) {
							const district = newCity.districts?.find(
								(dist) =>
									dist.slug === districtParam ||
									String(dist.id) === districtParam,
							);
							if (district?.id) {
								dispatch(setUserCurrentDistrict(district));
							} else {
								dispatch(setUserCurrentDistrict({}));
								const cleanPath = location.pathname.replace(
									cityURLParam,
									cityParam,
								);
								navigate(
									`${cleanPath}${location.search}${location.hash}`,
									{ replace: true },
								);
							}
						}
					} else {
						navigate('/404');
					}
					dispatch(setCountry(cities));
					dispatch(setRegion(citiesData[selectedCountryKey].name));
				}
			})
			.catch((error) => {
				console.error('error: ', error);
				navigate('/404');
			})
			.finally(() => {
				if (setIsLoading) {
					setIsLoading(false);
				}
			});
	};

	useEffect(() => {
		if (setIsLoading) setIsLoading(true);
		if (!districtParam) {
			dispatch(setUserCurrentDistrict({}));
		}

		timeoutRef.current = setTimeout(() => {
			loadServices();
		}, 400);

		return () => {
			console.warn('=> abort prev services load. reason: new data');
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [cityParam, districtParam, dispatch, lang]);

	useEffect(() => {
		if (!city && allCities.length > 0) {
			setLinksCurrentCity(false);
			if (cityParam && containsKeyword(cityParam, slugOfAllCities)) {
				const cityBySlug = allCities.filter(
					(city) => city.slug === cityParam,
				)[0];
				dispatch(setCity(cityParam));
				if (cityBySlug) {
					dispatch(setCurrentCity(cityBySlug));
				}
			}
		}
	}, [allCities]);
};

export default useDefiningCity;
